import JOB_ROLE_TREE_OPTIONS from '@aibees/elephant/constants/job-role-tree-options.json';
import { humanReadableDepartment } from '@aibees/elephant/hooks/metrics/use-company-metrics';
import { CompanyDepartments } from '../../search-table-company/types';
import { SearchFilterTreeOptions } from '../types';

// for employee by department filter
export const departmentsOptions: SearchFilterTreeOptions[][] = [
  Object.keys(CompanyDepartments)
    .map((department, index) => {
      const label: string = humanReadableDepartment(
        department as CompanyDepartments
      );
      return {
        id: department,
        value: department,
        label,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label)),
];

// for headcount growth filter
export const departmentsOptionsForHeadcountGrowth: SearchFilterTreeOptions[][] =
  [
    [
      ...[
        {
          id: 'all',
          value: 'all',
          label: humanReadableDepartment('all' as CompanyDepartments),
        },
      ],
      ...Object.keys(CompanyDepartments)
        .map((department) => {
          const label: string = humanReadableDepartment(
            department as CompanyDepartments
          );
          return {
            id: department,
            value: department,
            label,
          };
        })
        .sort((a, b) => a.label.localeCompare(b.label)),
    ],
  ];

// for job role filter

export const jobRoleTreeOptions: SearchFilterTreeOptions[] =
  JOB_ROLE_TREE_OPTIONS;

export function getJobRoleTreeOptions() {
  const response = jobRoleTreeOptions.map((department) => ({
    ...department,
    children: department.children?.map((subDepartment) => ({
      ...subDepartment,
      value: `${department.value}::${subDepartment.value}`,
    })),
  }));
  return [response];
}

// here we get array of selected options values and return an array including those options plus all its children values
// the final array should be unique
export function mapJobRoleTreeOptionForBackend(selectedOptions: string[]) {
  const allValues = selectedOptions.flatMap((optionValue) => {
    const option = getJobRoleTreeOptions()
      .flat()
      .find((o) => o.value === optionValue);
    if (option) {
      const childrenValues =
        option.children?.map((subOption) => subOption.value) || [];
      return [option.value, ...childrenValues];
    }
    return [optionValue];
  });
  return Array.from(new Set(allValues));
}

export const JOB_FUNCTION_TRANSLATION: Record<string, string> = {
  // departments
  c_suite: 'C-suite',
  product_management: 'Product',
  master_engineering_technical: 'Engineering & Technical',
  design: 'Design',
  education: 'Education',
  master_finance: 'Finance',
  master_human_resources: 'Human Resources',
  master_information_technology: 'Information Technology',
  master_legal: 'Legal',
  master_marketing: 'Marketing',
  medical_health: 'Medical & Health',
  master_operations: 'Operations',
  master_sales: 'Sales',
  consulting: 'Consulting',
  // sub-departments
  executive: 'Executive',
  founder: 'Founder',
  product_development: 'Product Development',
  product_mangement: 'Product Management',
  artificial_intelligence_machine_learning:
    'Artificial Intelligence / Machine Learning',
  bioengineering: 'Bioengineering',
  biometrics: 'Biometrics',
  business_intelligence: 'Business Intelligence',
  chemical_engineering: 'Chemical Engineering',
  cloud_mobility: 'Cloud / Mobility',
  data_science: 'Data Science',
  devops: 'DevOps',
  emerging_technology_innovation: 'Emerging Technology / Innovation',
  engineering_technical: 'Engineering & Technical',
  industrial_engineering: 'Industrial Engineering',
  mechanic: 'Mechanic',
  mobile_development: 'Mobile Development',
  project_management: 'Project Management',
  research_development: 'Research & Development',
  scrum_master_agile_coach: 'Scrum Master / Agile Coach',
  software_development: 'Software Development',
  support_technical_services: 'Support / Technical Services',
  technician: 'Technician',
  technology_operations: 'Technology Operations',
  test_quality_assurance: 'Test / Quality Assurance',
  ui_ux: 'UI / UX',
  web_development: 'Web Development',
  marketing: 'Marketing',
  digital_marketing: 'Digital Marketing',
  all_design: 'All Design',
  product_ui_ux_design: 'Product or UI/UX Design',
  graphic_design: 'Graphic / Visual / Brand Design',
  teacher: 'Teacher',
  principal: 'Principal',
  superintendent: 'Superintendent',
  professor: 'Professor',
  accounting: 'Accounting',
  finance: 'Finance',
  financial_planning_analysis: 'Financial Planning & Analysis',
  financial_reporting: 'Financial Reporting',
  financial_strategy: 'Financial Strategy',
  financial_systems: 'Financial Systems',
  internal_audit_control: 'Internal Audit & Control',
  investor_relations: 'Investor Relations',
  mergers_acquisitions: 'Mergers & Acquisitions',
  financial_risk: 'Financial Risk',
  sourcing_procurement: 'Sourcing / Procurement',
  tax: 'Tax',
  treasury: 'Treasury',
  compensation_benefits: 'Compensation & Benefits',
  culture_diversity_inclusion: 'Culture, Diversity & Inclusion',
  employee_labor_relations: 'Employee & Labor Relations',
  health_safety: 'Health & Safety',
  human_resource_information_system: 'Human Resource / Information System',
  human_resources: 'Human Resources',
  hr_business_partner: 'HR Business Partner',
  learning_development: 'Learning & Development',
  organizational_development: 'Organizational Development',
  recruiting_talent_acquisition: 'Recruiting & Talent Acquisition',
  talent_management: 'Talent Management',
  workforce_mangement: 'Workforce Management',
  application_development: 'Application Development',
  business_service_management_itsm: 'Business Service Management / ITSM',
  collaboration_web_app: 'Collaboration & Web App',
  data_center: 'Data Center',
  data_warehouse: 'Data Warehouse',
  database_administration: 'Database Administration',
  ecommerce_development: 'eCommerce Development',
  enterprise_architecture: 'Enterprise Architecture',
  help_desk_desktop_services: 'Help Desk / Desktop Services',
  hr_financial_erp_systems: 'HR / Financial / ERP Systems',
  information_security: 'Information Security',
  information_technology: 'Information Technology',
  infrastructure: 'Infrastructure',
  it_asset_management: 'IT Asset Management',
  it_audit_it_compliance: 'IT Audit / IT Compliance',
  it_operations: 'IT Operations',
  it_procurement: 'IT Procurement',
  it_strategy: 'IT Strategy',
  networking: 'Networking',
  project_program_management: 'Project & Program Management',
  quality_assurance: 'Quality Assurance',
  retail_store_systems: 'Retail / Store Systems',
  servers: 'Servers',
  storage_disaster_recovery: 'Storage & Disaster Recovery',
  telecommunications: 'Telecommunications',
  virtualization: 'Virtualization',
  compliance: 'Compliance',
  contracts: 'Contracts',
  ediscovery: 'eDiscovery',
  governmental_affairs_regulatory_law: 'Governmental Affairs & Regulatory Law',
  intellectual_property_patent: 'Intellectual Property & Patent',
  labor_employment: 'Labor & Employment',
  lawyer_attorney: 'Lawyer / Attorney',
  legal: 'Legal',
  legal_counsel: 'Legal Counsel',
  legal_operations: 'Legal Operations',
  litigation: 'Litigation',
  privacy: 'Privacy',
  advertising: 'Advertising',
  brand_management: 'Brand Management',
  content_marketing: 'Content Marketing',
  customer_experience: 'Customer Experience',
  customer_marketing: 'Customer Marketing',
  demand_generation: 'Demand Generation',
  ecommerce_marketing: 'eCommerce Marketing',
  event_marketing: 'Event Marketing',
  field_marketing: 'Field Marketing',
  lead_generation: 'Lead Generation',
  marketing_analytics_insights: 'Marketing Analytics / Insights',
  marketing_communications: 'Marketing Communications',
  product_marketing: 'Product Marketing',
  public_relations: 'Public Relations',
  search_engine_optimization_pay_per_click:
    'Search Engine Optimization / Pay Per Click',
  social_media_marketing: 'Social Media Marketing',
  strategic_communications: 'Strategic Communications',
  technical_marketing: 'Technical Marketing',
  sales: 'Sales',
  field_outside_sales: 'Field / Outside Sales',
  chiropractics: 'Chiropractics',
  dentistry: 'Dentistry',
  doctors_physicians: 'Doctors / Physicians',
  first_responder: 'First Responder',
  infectious_disease: 'Infectious Disease',
  medical_administration: 'Medical Administration',
  medical_education_training: 'Medical Education & Training',
  medicine: 'Medicine',
  neurology: 'Neurology',
  nursing: 'Nursing',
  nutrition_dietetics: 'Nutrition & Dietetics',
  oncology: 'Oncology',
  pathology: 'Pathology',
  pharmacy: 'Pharmacy',
  physical_therapy: 'Physical Therapy',
  psychology: 'Psychology',
  public_health: 'Public Health',
  radiology: 'Radiology',
  social_work: 'Social Work',
  call_center: 'Call Center',
  construction: 'Construction',
  corporate_strategy: 'Corporate Strategy',
  customer_service_support: 'Customer Service / Support',
  enterprise_resource_planning: 'Enterprise Resource Planning',
  facilities_management: 'Facilities Management',
  leasing: 'Leasing',
  logistics: 'Logistics',
  office_operations: 'Office Operations',
  operations: 'Operations',
  physical_security: 'Physical Security',
  project_development: 'Project Development',
  quality_management: 'Quality Management',
  real_estate: 'Real Estate',
  store_operations: 'Store Operations',
  supply_chain: 'Supply Chain',
  customer_success: 'Customer Success',
  account_management: 'Account Management',
  business_development: 'Business Development',
  channel_sales: 'Channel Sales',
  customer_retention_development: 'Customer Retention & Development',
  inside_sales: 'Inside Sales',
  partnerships: 'Partnerships',
  sales_enablement: 'Sales Enablement',
  sales_engineering: 'Sales Engineering',
  sales_operations: 'Sales Operations',
  sales_training: 'Sales Training',
  consultant: 'Consultant',
};
